import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../lib/supabase";

interface AdminLoginProps {
  onLogin: () => void;
}

export default function AdminLogin({ onLogin }: AdminLoginProps) {
  const { t } = useTranslation("admin");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: authError } = await supabase.auth.signInWithPassword({
      email,
      password: pw,
    });
    setSubmitting(false);
    if (authError) {
      setError(t("login.invalidCredentials"));
      setPw("");
      return;
    }
    // 認証成功。admin 許可の最終判定は AdminPage 側の getSession + allowlist で行う。
    onLogin();
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FAF5E8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: 20,
          padding: "48px 40px",
          width: "100%",
          maxWidth: 400,
          border: "1px solid rgba(44,95,79,0.1)",
        }}
      >
        <div className="flex items-center gap-3 mb-8">
          <div
            className="flex items-center justify-center w-10 h-10"
            style={{ borderRadius: 12, background: "rgba(44,95,79,0.08)" }}
          >
            <i className="ri-lock-line" style={{ fontSize: 18, color: "#2C5F4F" }} />
          </div>
          <div>
            <p
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.12em",
                color: "#2C5F4F",
                textTransform: "uppercase",
              }}
            >
              Admin
            </p>
            <h1
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontWeight: 700,
                fontSize: 20,
                color: "#1A1A1A",
              }}
            >
              {t("login.title")}
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label
              htmlFor="admin-email"
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: "#444444",
                display: "block",
                marginBottom: 8,
              }}
            >
              {t("login.email")}
            </label>
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(null); }}
              placeholder="admin@example.com"
              autoComplete="email"
              required
              style={{
                width: "100%",
                padding: "11px 14px",
                borderRadius: 10,
                border: error
                  ? "1.5px solid #dc2626"
                  : "1.5px solid rgba(44,95,79,0.2)",
                fontFamily: "'Noto Sans JP', sans-serif",
                fontSize: 14,
                color: "#1A1A1A",
                background: "#FDFAF3",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div>
            <label
              htmlFor="admin-pw"
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: "#444444",
                display: "block",
                marginBottom: 8,
              }}
            >
              {t("login.password")}
            </label>
            <div style={{ position: "relative" }}>
              <input
                id="admin-pw"
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => { setPw(e.target.value); setError(null); }}
                placeholder={t("login.passwordPlaceholder")}
                autoComplete="current-password"
                required
                style={{
                  width: "100%",
                  padding: "11px 44px 11px 14px",
                  borderRadius: 10,
                  border: error
                    ? "1.5px solid #dc2626"
                    : "1.5px solid rgba(44,95,79,0.2)",
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: 14,
                  color: "#1A1A1A",
                  background: "#FDFAF3",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="cursor-pointer flex items-center justify-center w-8 h-8"
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "#888888",
                }}
              >
                <i className={show ? "ri-eye-off-line" : "ri-eye-line"} style={{ fontSize: 16 }} />
              </button>
            </div>
            {error && (
              <p
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: 12,
                  color: "#dc2626",
                  marginTop: 6,
                }}
              >
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="whitespace-nowrap cursor-pointer w-full"
            style={{
              padding: "12px",
              borderRadius: 10,
              background: "#2C5F4F",
              border: "none",
              fontFamily: "'Noto Sans JP', sans-serif",
              fontWeight: 700,
              fontSize: 14,
              color: "#ffffff",
              marginTop: 4,
              opacity: submitting ? 0.7 : 1,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = submitting ? "0.7" : "0.85")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = submitting ? "0.7" : "1")}
          >
            {submitting ? t("login.submitting") : t("login.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
